import React, { useState, useMemo, useEffect, useRef } from 'react';
import { fmtMoney, fmtPct, Icon, TypePill, Disclosure, SummaryBar } from './PLComponents';

// =============== Tree utilities ===============
function flattenTree(tree, expandedSet, depth = 0, parent = null, acc = []) {
  for (const node of tree) {
    const row = { ...node, depth, parent };
    acc.push(row);
    if (node.children && expandedSet.has(node.id)) {
      flattenTree(node.children, expandedSet, depth + 1, node, acc);
    }
  }
  return acc;
}

// Revenue deduction keys — subtracted from their parent's sum instead of added.
// Matches bare lineKeys ('refunds') AND custom item keys ('cust::income::refunds').
const DEDUCTION_KEYS = new Set(['refunds']);
const isDeductionKey = (id) => DEDUCTION_KEYS.has(id) || DEDUCTION_KEYS.has(id.split('::').pop());

function computeMonth(tree, month, apiValues, periodStatus) {
  const out = {};
  // Scenario B: Use API values only for non-finalized periods
  const isFinalized = ['submitted', 'locked'].includes(periodStatus);
  
  function walk(nodes) {
    for (const n of nodes) {
      if (n.children && n.children.length) {
        walk(n.children);
        const sum = n.children.reduce((s, c) => {
          const sign = isDeductionKey(c.id) ? -1 : 1;
          return s + sign * (out[c.id] || 0);
        }, 0);
        if (sum === 0 && n.values && n.values[month]) {
          out[n.id] = n.values[month];
        } else {
          out[n.id] = sum;
        }
      } else if (n.values) {
        // For finalized periods: always use persisted manual values (Scenario B - audit integrity)
        // For draft periods: use API value if apiDataKey is set, otherwise use manual value
        const useApiValue = n.apiDataKey && !isFinalized;
        out[n.id] = useApiValue ? ((apiValues?.[n.apiDataKey]) ?? 0) : (n.values[month] || 0);
      } else {
        out[n.id] = 0;
      }
    }
  }
  walk(tree);
  // Gross income (before deductions) — used as base for income sub-item % calculations
  const incomeNode = tree.find ? tree.find(n => n.id === 'income') : null;
  if (incomeNode && incomeNode.children) {
    const deductionSum = incomeNode.children
      .filter(c => isDeductionKey(c.id))
      .reduce((s, c) => s + Math.abs(out[c.id] || 0), 0);
    out.__gross_income = (out.income || 0) + deductionSum;
  } else {
    out.__gross_income = out.income || 0;
  }
  out.gross_profit = (out.income || 0) - (out.cogs || 0);
  out.net_income   = (out.gross_profit || 0) - (out.expenses || 0);
  return out;
}

function yearTotals(tree, months, apiValues, periodStatusMap, year) {
  const sums = {};
  for (const m of months) {
    const monthKey = `${m}-${year}`;
    const status = periodStatusMap?.[monthKey] || 'not-started';
    const vals = computeMonth(tree, m, apiValues, status);
    for (const k in vals) sums[k] = (sums[k] || 0) + vals[k];
  }
  return sums;
}

function collectNodes(tree, map = {}) {
  for (const n of tree) {
    map[n.id] = n;
    if (n.children) collectNodes(n.children, map);
  }
  return map;
}

// =============== Row component ===============
function PLRow({ row, computedByMonth, expanded, toggleExpand, onEditCell, onBeforeEdit, onAddChild, onDelete, onSelectRow = () => {}, monthsShown, showPct, showTypes, locked, mode, isAdmin, schemas, validationAttempted }) {
  const hasChildren = row.children && row.children.length > 0;
  const isLeaf = !hasChildren;
  // A calculated parent is "locked" (read-only, value = sum) only when it has required children.
  // If it has only optional user-added sub-items, it stays directly editable.
  const hasRequiredChildren = hasChildren && row.children.some(c => c.required);
  const isCalculatedParent = hasRequiredChildren;
  const simplifiedEditableMajors = new Set(['cogs', 'expenses']);
  const simplifiedDirectEntry = mode === 'simplified' && simplifiedEditableMajors.has(row.id);
  const isDeduction = isDeductionKey(row.id);
  // A 'calculated' node is editable directly (franchisee can enter a total).
  // These 5 top-level rows are always read-only — computed from children (sum or formula)
  const FORMULA_ROWS = new Set(['income', 'cogs', 'gross_profit', 'expenses', 'net_income']);
  const editable = !locked && !row.apiDataKey && !FORMULA_ROWS.has(row.id) && (
    simplifiedDirectEntry ||
    ((row.type === 'manual' || row.type === 'custom') && !isCalculatedParent) ||
    (row.type === 'calculated' && !isCalculatedParent)
  );
  const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const existingChildSlugs = new Set((row.children || []).map(c => toSlug(c.label || '')));
  // Resolve available schema items:
  // - Regular section node (e.g. 'income'): L2 items from schemas[row.id]
  // - Calculated custom node (e.g. 'cust::income::other_income'): L3 sub-items
  let availableSchemaItems = [];
  if (schemas) {
    if (row.id.startsWith('cust::')) {
      const parts = row.id.split('::'); // ['cust', sectionKey, parentSlug, ...]
      if (parts.length === 3) {
        const sectionKey = parts[1];
        const parentSlug = parts[2];
        const sectionItems = schemas[sectionKey] || [];
        const calcItem = sectionItems.find(i => !i.parentGroupName && toSlug(i.name) === parentSlug);
        if (calcItem) {
          availableSchemaItems = sectionItems.filter(
            s => s.parentGroupName?.trim() === calcItem.name && s.name?.trim() && !existingChildSlugs.has(toSlug(s.name))
          );
        }
      }
    } else {
      availableSchemaItems = (schemas[row.id] || []).filter(
        s => s.name?.trim() && !s.parentGroupName?.trim() && !existingChildSlugs.has(toSlug(s.name))
      );
    }
  }
  // Admins can always add (even to configure new items); non-admins only if schema items are available
  const canAddChild = !locked && mode !== 'simplified' &&
    (row.type === 'manual' || row.type === 'calculated' || row.type === 'custom' || row.type === 'system') &&
    (isAdmin ? row.children !== undefined : availableSchemaItems.length > 0);
  // Only user-added optional sub-items (type='custom', not required) are deletable.
  // Calculated schema parents (Utilities, Fringe Benefits etc.) and required items are never deletable.
  const canDelete = !locked && mode !== 'simplified' && row.type === 'custom' && !row.required;

  // Look up configured max value for this row from schemas (custom rows only)
  let rowMax = null;
  if (schemas && row.id.startsWith('cust::')) {
    const parts = row.id.split('::');
    if (parts.length === 3) {
      // L2: cust::<sectionKey>::<slug>
      const sectionKey = parts[1];
      const slug = parts[2];
      const match = (schemas[sectionKey] || []).find(i => !i.parentGroupName && toSlug(i.name) === slug);
      if (match?.max != null) rowMax = match.max;
    } else if (parts.length === 4) {
      // L3: cust::<sectionKey>::<parentSlug>::<slug>
      const sectionKey = parts[1];
      const slug = parts[3];
      const match = (schemas[sectionKey] || []).find(i => i.parentGroupName && toSlug(i.name) === slug);
      if (match?.max != null) rowMax = match.max;
    }
  }

  const rowClass = [
    row.major ? 'row-major' : '',
    row.accent ? 'row-accent' : '',
    simplifiedDirectEntry ? 'row-manual' : (row.type === 'calculated' || isCalculatedParent ? 'row-calculated' : ''),
    row.type === 'manual' && !simplifiedDirectEntry ? 'row-manual' : '',
    row.type === 'system' ? 'row-system' : '',
  ].filter(Boolean).join(' ');

  const indent = row.depth * 18;

  return (
    <tr className={rowClass}>
      <td className="col-category" style={{ paddingLeft: 14 + indent }}>
        <div className="cat-cell">
          <Disclosure open={expanded} onClick={() => toggleExpand(row.id)} leaf={isLeaf} />
          <div className="cat-label" onClick={(e) => { if (!isLeaf) { e.stopPropagation(); toggleExpand(row.id); } }} style={{ cursor: isLeaf ? 'default' : 'pointer' }}>
            <span className="cat-text">
              {row.label}
              {row.required && !row.major && <span className="required-indicator"> *</span>}
            </span>
            {row.locked && <span className="lock-badge" data-tip="Locked / auto-populated"><Icon.lock /></span>}
          </div>
          <div className="row-actions">
            {canAddChild && (
              <button className="row-act row-act-add" data-tip="Add line item" onClick={(e) => { e.stopPropagation(); onAddChild(row); }}>
                <Icon.plus />
              </button>
            )}
            {/* <button className="row-act" data-tip="Details" onClick={() => onSelectRow(row)}>
              <Icon.info />
            </button> */}
            <button
              className="row-act danger"
              data-tip={canDelete ? 'Delete line' : 'Required — cannot delete'}
              disabled={!canDelete}
              onClick={(e) => { e.stopPropagation(); canDelete && onDelete(row); }}
            >
              <Icon.trash />
            </button>
          </div>
        </div>
      </td>
      {monthsShown.map((m) => {
        const v = computedByMonth[m]?.[row.id] ?? 0;
        const isIncomeChild = row.parent?.id === 'income';
        const rev = isIncomeChild
          ? (computedByMonth[m]?.__gross_income ?? computedByMonth[m]?.income ?? 0)
          : (computedByMonth[m]?.income ?? 0);
        const pct = rev ? (Math.abs(v) / rev) * 100 : 0;
        return (
          <React.Fragment key={m}>
            <NumCell value={v} editable={editable} row={row} month={m} onEdit={onEditCell} onBeforeEdit={onBeforeEdit ? (openFn) => onBeforeEdit(row, m, openFn) : undefined} validationAttempted={validationAttempted} max={rowMax} parens={isDeduction} />
            <td className={`pct col-pct ${v < 0 ? 'neg' : ''}`} onClick={(e) => e.stopPropagation()}>
              {rev && v !== 0 ? (isDeduction ? '(' + pct.toFixed(2) + '%)' : pct.toFixed(2) + '%') : '—'}
            </td>
          </React.Fragment>
        );
      })}
    </tr>
  );
}

function NumCell({ value, editable, row, month, onEdit, onBeforeEdit, className, validationAttempted, max, parens }) {
  const [draft, setDraft] = useState(null); // null = not focused
  const [negWarn, setNegWarn] = useState(false);
  const [maxWarn, setMaxWarn] = useState(false);
  const modalPendingRef = useRef(false); // true when confirmation modal is open - use ref for synchronous updates
  const skipInterceptRef = useRef(false); // true after modal confirmation - skip onBeforeEdit on re-focus
  const inputRef = useRef(null);

  // Check if this is an empty required field after validation attempt
  const isInvalid = validationAttempted &&
                    row.required &&
                    !row.apiDataKey &&
                    (value == null || value === 0) &&
                    (
                      // Regular editable leaf: manual or custom with no children
                      ((row.type === 'manual' || row.type === 'custom') && !row.children?.length) ||
                      // Calculated parent that is required but has only optional children (direct-entry mode)
                      (row.type === 'calculated' && !(row.children || []).some(c => c.required))
                    );

  const commit = (rawVal) => {
    // Don't commit if a confirmation modal is pending user response
    if (modalPendingRef.current) {
      modalPendingRef.current = false;
      return true;
    }
    const cleaned = (rawVal ?? draft ?? '').replace(/[^0-9.\-]/g, '');
    // Empty input — just reset, nothing to save
    if (!cleaned) { setNegWarn(false); setMaxWarn(false); setDraft(null); return true; }
    const n = parseFloat(parseFloat(cleaned).toFixed(2));
    if (!isNaN(n) && n < 0) {
      setNegWarn(true);
      setMaxWarn(false);
      return false; // Keep draft visible so user sees the bad value and can correct it
    }
    if (!isNaN(n) && max != null && n > max) {
      setMaxWarn(true);
      setNegWarn(false);
      return false; // Keep draft visible so user sees the bad value and can correct it
    }
    if (!isNaN(n) && n !== value) onEdit(row.id, month, n);
    setNegWarn(false);
    setMaxWarn(false);
    setDraft(null);
    return true;
  };

  const cls = `num col-month num-cell ${className || ''} ${editable ? 'editable' : 'readonly'} ${row.apiDataKey ? 'auto-populated' : ''} ${value < 0 ? 'neg' : ''} ${isInvalid ? 'validation-error' : ''}`;

  // EDITABLE: always render as a real input (no display→edit toggle)
  if (editable) {
    const displayVal = draft !== null ? draft : (value || '');
    const handleFocus = (e) => {
      if (onBeforeEdit && !skipInterceptRef.current) {
        // Normal path: intercept to check if confirmation is needed
        let calledSync = false;
        modalPendingRef.current = true; // Set flag before calling onBeforeEdit
        onBeforeEdit(() => {
          calledSync = true;
          modalPendingRef.current = false;
          if (document.activeElement === inputRef.current) {
            // Called synchronously — input is still focused, open it directly
            setDraft(String(value || ''));
            e.target.select();
          } else {
            // Called asynchronously after modal confirmation — input was blurred.
            // Set skip flag so the re-focus doesn't re-trigger the modal check.
            skipInterceptRef.current = true;
            inputRef.current?.focus();
          }
        });
        if (!calledSync) {
          // Modal shown — blur the input; re-focus happens when user clicks Continue
          e.target.blur();
        } else {
          modalPendingRef.current = false; // No modal - clear flag
        }
      } else {
        // Re-focus path: called after modal confirmation (skipInterceptRef=true), or no onBeforeEdit
        skipInterceptRef.current = false; // Reset so next click checks again
        setDraft(String(value || ''));
        e.target.select();
      }
    };
    return (
      <td className={cls} title={row.apiDataKey ? `Auto-populated · API key: ${row.apiDataKey}` : undefined} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <div className={`num-input-wrapper${negWarn || maxWarn ? ' num-input-error' : ''}${isInvalid ? ' num-input-invalid' : ''}`} onClick={(e) => { e.stopPropagation(); inputRef.current?.focus(); }} onMouseDown={(e) => e.stopPropagation()}>
          {parens && value !== 0 && <span className="deduction-paren">(</span>}
          <span className="currency-symbol">$</span>
          <input
            ref={inputRef}
            className="num-input"
            value={draft !== null ? draft : (value === 0 ? '0.00' : (value || '0.00'))}
            onFocus={handleFocus}
            onChange={(e) => { setDraft(e.target.value); setNegWarn(false); setMaxWarn(false); }}
            onBlur={(e) => { commit(e.target.value); }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.target.blur(); }
              if (e.key === 'Escape') { setNegWarn(false); setMaxWarn(false); setDraft(null); e.target.blur(); }
              e.stopPropagation();
            }}
          />
          {parens && value !== 0 && <span className="deduction-paren">)</span>}
        </div>
        {negWarn && (
          <div className="num-neg-warn">Negative values are not allowed</div>
        )}
        {maxWarn && (
          <div className="num-neg-warn">Exceeds maximum allowed (${max?.toLocaleString()})</div>
        )}
      </td>
    );
  }

  // READ-ONLY: styled display box
  return (
    <td className={cls} title={row.apiDataKey ? `Auto-populated · API key: ${row.apiDataKey}` : undefined} onClick={(e) => e.stopPropagation()}>
      <div className="num-display-wrapper">
        {parens && value !== 0 && <span className="deduction-paren">(</span>}
        <span className="currency-symbol">$</span>
        <span className="num-value">{value === 0 ? '0.00' : (fmtMoney(value || 0).replace('$', '').trim())}</span>
        {parens && value !== 0 && <span className="deduction-paren">)</span>}
      </div>
    </td>
  );
}

// =============== Add-line modal ===============
function AddLineModal({ parent, schema = [], existingChildren = [], currentMonth, onCancel, onAdd }) {
  const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const addedSlugs = new Set(existingChildren.map(c => toSlug(c.label || '')));
  // schema is pre-filtered by PLApp for the correct level (L2 or L3 sub-items)
  const validSchema = schema.filter(s => s.name && s.name.trim() && !addedSlugs.has(toSlug(s.name)));
  const [pickedIdx, setPickedIdx] = useState(0);
  const [value, setValue] = useState('');
  const [valueErr, setValueErr] = useState('');
  const picked = validSchema[pickedIdx];
  const isCalc = !!picked?.isCalculated;
  const isApiMapped = !isCalc && !!picked?.apiDataKey;

  const onConfirm = () => {
    if (!picked) return;
    if (!isCalc && !isApiMapped) {
      const num = parseFloat(value || '0') || 0;
      if (picked.fieldType === 'number') {
        if (num < 0) { setValueErr('Value cannot be negative'); return; }
        if (picked.max != null && num > picked.max) { setValueErr(`Value exceeds max allowed ($${picked.max.toLocaleString()})`); return; }
      }
      onAdd(picked.name, num);
    } else {
      onAdd(picked.name, 0);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3 className="modal-title">Add line item</h3>
          <div className="modal-subtitle">Under <strong>{parent.label}</strong>. Allowed items are configured globally by admin.</div>
        </div>
        <div className="modal-body">
          {validSchema.length === 0 ? (
            <div className="empty-schema">
              <Icon.info /> {schema.length === 0
                ? <span>No items configured for this section. Contact your admin to add allowed line items for <strong>{parent.label}</strong>.</span>
                : <span>All configured items for <strong>{parent.label}</strong> are already in your P&amp;L table. Scroll down to see them, or ask your admin to configure additional items.</span>
              }
            </div>
          ) : (
            <>
              <div className="field-group">
                <label>Line item</label>
                <select value={pickedIdx} onChange={(e) => { setPickedIdx(+e.target.value); setValueErr(''); }}>
                  {validSchema.map((s, i) => <option key={i} value={i}>{s.name}</option>)}
                </select>
                {picked && (
                  <div className="add-item-meta">
                    <span className="add-item-type-badge">{picked.isCalculated ? 'Calculated' : (picked.fieldType === 'number' ? 'Number' : 'Text')}</span>
                    <span className={`add-item-req-badge ${picked.isRequired ? 'required' : 'optional'}`}>
                      {picked.isRequired ? 'Required' : 'Optional'}
                    </span>
                    {!picked.isCalculated && picked.max != null && <span className="add-item-max">Max: ${picked.max.toLocaleString()}</span>}
                  </div>
                )}
              </div>
              {isCalc ? (
                <div className="field-group">
                  <div className="add-item-calc-note"><Icon.info /> This item is <strong>Calculated</strong> — its value will be the sum of its sub-items. Sub-items can be added after this row appears.</div>
                </div>
              ) : isApiMapped ? (
                <div className="field-group">
                  <div className="add-item-calc-note"><Icon.info /> This item is <strong>auto-populated</strong> from the API. The value will be fetched automatically — no manual entry needed.</div>
                </div>
              ) : (
                <div className="field-group">
                  <label>Amount for {currentMonth}</label>
                  <input
                    type={picked?.fieldType === 'number' ? 'number' : 'text'}
                    placeholder="0"
                    value={value}
                    onChange={(e) => { setValue(e.target.value); setValueErr(''); }}
                  />
                  {valueErr && <div className="field-error">{valueErr}</div>}
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn primary" disabled={!picked} onClick={onConfirm}>Add line</button>
        </div>
      </div>
    </div>
  );
}

// =============== Admin: Custom Items Config Panel ===============
// Hierarchy:
//   Level 2 (direct child of P&L section): can be Manual or Calculated
//   Level 3 (sub-item of a Calculated L2): always Manual, no toggle
// Items at sections with sectionDepth >= 2 are always Manual (already deep in tree)

function CustomItemsConfigPanel({ schemas, sections, onUpdate, onDone, onClose }) {
  const [activeSection, setActiveSection] = useState(() => sections && sections.length > 0 ? sections[0].id : null);
  const [draftSchemas, setDraftSchemas] = useState(() => ({ ...schemas }));
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { label, onConfirm }

  useEffect(() => {
    if (sections && sections.length > 0 && !activeSection) {
      setActiveSection(sections[0].id);
    }
  }, [sections]);

  const items = activeSection ? (draftSchemas[activeSection] || []) : [];
  const activeSectionMeta = sections && sections.length > 0
    ? (sections.find(s => s.id === activeSection) || sections[0])
    : null;
  const isLeaf = activeSectionMeta?.isLeaf;
  const nodeType = activeSectionMeta?.nodeType;

  // Sections at depth >= 2 in the real P&L tree are already Level 3 — all items are always Manual
  const sectionDepth = activeSectionMeta?.depth || 0;
  const canBeCalculated = sectionDepth < 2;

  const addRow = () => {
    const section = activeSection || (sections && sections.length > 0 ? sections[0].id : null);
    if (!section) return;
    setDraftSchemas(prev => ({
      ...prev,
      [section]: [...(prev[section] || []), { name: '', fieldType: 'number', maxLength: 9, max: 1000, isRequired: false, isCalculated: false, parentGroupName: null }],
    }));
  };
  const updateRow = (i, patch) => {
    const section = activeSection || (sections && sections.length > 0 ? sections[0].id : null);
    if (!section) return;
    setDraftSchemas(prev => ({ ...prev, [section]: (prev[section] || []).map((r, idx) => idx === i ? { ...r, ...patch } : r) }));
  };
  const removeRow = (i) => {
    const section = activeSection || (sections && sections.length > 0 ? sections[0].id : null);
    if (!section) return;
    const item = (draftSchemas[section] || [])[i];
    if (!item) return;
    setDeleteConfirm({
      label: item.name || 'this item',
      onConfirm: () => {
        setDraftSchemas(prev => {
          const current = prev[section] || [];
          const removing = current[i];
          const filtered = current.filter((it, idx) => {
            if (idx === i) return false;
            if (removing && !removing.parentGroupName && removing.isCalculated) {
              if (it.parentGroupName?.trim() === removing.name?.trim()) return false;
            }
            return true;
          });
          return { ...prev, [section]: filtered };
        });
        setDeleteConfirm(null);
      },
    });
  };

  const addSubRow = (parentItemName) => {
    const section = activeSection || (sections && sections.length > 0 ? sections[0].id : null);
    if (!section) return;
    setDraftSchemas(prev => ({
      ...prev,
      [section]: [...(prev[section] || []), { name: '', fieldType: 'number', maxLength: 9, max: 1000, isRequired: false, isCalculated: false, parentGroupName: parentItemName }],
    }));
  };

  const removeSubRow = (parentItemName, subIdx) => {
    const section = activeSection || (sections && sections.length > 0 ? sections[0].id : null);
    if (!section) return;
    const subItems = (draftSchemas[section] || []).filter(it => it.parentGroupName?.trim() === parentItemName);
    const item = subItems[subIdx];
    if (!item) return;
    setDeleteConfirm({
      label: item.name || 'this item',
      onConfirm: () => {
        setDraftSchemas(prev => {
          const current = prev[section] || [];
          const subs = current.filter(it => it.parentGroupName?.trim() === parentItemName);
          const toRemove = subs[subIdx];
          return { ...prev, [section]: current.filter(it => it !== toRemove) };
        });
        setDeleteConfirm(null);
      },
    });
  };

  const toggleCalculated = (i, makeCalculated) => {
    const section = activeSection || (sections && sections.length > 0 ? sections[0].id : null);
    if (!section) return;
    setDraftSchemas(prev => {
      const current = prev[section] || [];
      const item = current[i];
      const newCalculated = makeCalculated !== undefined ? makeCalculated : !item.isCalculated;
      const becomingManual = item.isCalculated && !newCalculated;
      let updated = current.map((it, idx) => idx === i ? { ...it, isCalculated: newCalculated } : it);
      // If turning off Calculated, remove all its sub-items
      if (becomingManual) {
        updated = updated.filter(it => it.parentGroupName?.trim() !== item.name?.trim());
      }
      return { ...prev, [section]: updated };
    });
  };

  useEffect(() => {
    if (isLeaf && nodeType !== 'calculated' && (draftSchemas[activeSection] || []).length === 0 && activeSectionMeta) {
      setDraftSchemas(prev => ({
        ...prev,
        [activeSection]: [{ name: activeSectionMeta.label, fieldType: 'number', maxLength: 9, max: 50000, isRequired: false, parentGroupName: '' }],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, isLeaf, nodeType]);

  const handleSave = async () => {
    // Normalize items: preserve parentGroupName and isCalculated correctly
    const normalize = (arr) => (arr || [])
      .filter(item => item.name && item.name.trim() !== '')
      .map(item => ({
        ...item,
        parentGroupName: item.parentGroupName?.trim() || null,
        isCalculated: item.parentGroupName?.trim() ? false : !!item.isCalculated,
      }));

    const changedSections = Object.entries(draftSchemas).filter(([sectionKey, draftItems]) => {
      const fresh = normalize(draftItems);
      const orig = normalize(schemas[sectionKey] || []);
      return JSON.stringify(fresh) !== JSON.stringify(orig);
    });

    try {
      for (const [sectionKey, draftItems] of changedSections) {
        await onUpdate(sectionKey, normalize(draftItems));
      }
      if (changedSections.length > 0 && onDone) await onDone();
      onClose();
    } catch (e) {
      console.error('Failed to save custom schema:', e);
      // Show error inline — do NOT close the panel so user can retry
    }
  };

  const crumbs = useMemo(() => {
    if (!activeSectionMeta) return [];
    const path = [];
    let cur = activeSectionMeta;
    while (cur) {
      path.unshift(cur);
      cur = cur.parentId ? sections.find(s => s.id === cur.parentId) : null;
    }
    return path;
  }, [activeSectionMeta, sections]);

  const isCalculatedLeaf = isLeaf && nodeType === 'calculated';
  const isFieldConfigMode = isLeaf && !isCalculatedLeaf;
  const isGroupAddMode = !isLeaf;



  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal cic-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Custom line items</h3>
            <div className="modal-subtitle">Configure which custom items franchisees may add under each section.</div>
          </div>
          <button className="btn-icon-sm" onClick={onClose} title="Close" style={{ flexShrink: 0, marginTop: 2 }}>✕</button>
        </div>
        <div className="cic-body">
          <aside className="cic-sidebar">
            <div className="cic-sidebar-title">P&amp;L Hierarchy</div>
            {sections && sections.length > 0 ? (
              sections.map(s => {
                const sItems = draftSchemas[s.id] || [];
                // Build display entries: group items by parentGroupName
                // Direct items (no group) shown as leaves; grouped items shown under a group header
                const hasSubEntries = sItems.length > 0;
                // Only show L2 items in sidebar (parentGroupName = null); L3s shown nested under their L2
                // Filter out items with empty names to prevent empty lines in sidebar
                const l2Items = sItems.filter(it => !it.parentGroupName?.trim() && it.name?.trim());
                return (
                  <React.Fragment key={s.id}>
                    <button
                      className={`cic-tab cic-tab-depth-${s.depth || 0} ${activeSection === s.id ? 'active' : ''}`}
                      onClick={() => setActiveSection(s.id)}
                    >
                      <span className="cic-tab-tree" aria-hidden="true">
                        {s.depth > 0 && <span className="cic-tab-tree-glyph">{s.lastChild ? '└' : '├'}</span>}
                      </span>
                      <span className="cic-tab-label" style={{ fontWeight: s.depth === 0 ? 600 : 500 }}>{s.label}</span>
                      {hasSubEntries
                        ? <span className="cic-count">{sItems.length}</span>
                        : s.nodeType === 'calculated'
                          ? <span className="cic-tag">∑</span>
                          : <span className="cic-tag">·</span>
                      }
                    </button>
                    {/* L2 items, and L3 sub-items nested under Calculated parents */}
                    {l2Items.map((it, gi) => {
                      // Filter out sub-items with empty names to prevent empty lines
                      const subItems = sItems.filter(s => s.parentGroupName?.trim() === it.name?.trim() && s.name?.trim());
                      return (
                        <React.Fragment key={it.name + gi}>
                          <div className={`cic-tab cic-tab-depth-${(s.depth || 0) + 1}`} style={{ pointerEvents: 'none', opacity: 0.7 }}>
                            <span className="cic-tab-tree" aria-hidden="true">
                              <span className="cic-tab-tree-glyph">{gi === l2Items.length - 1 && subItems.length === 0 ? '└' : '├'}</span>
                            </span>
                            <span className="cic-tab-label">{it.name}</span>
                            {it.isCalculated && <span className="cic-tag" style={{ color: 'var(--ink-3)', fontSize: 9 }}>∑</span>}
                            {it.isRequired && <span className="cic-tag" style={{ color: 'var(--accent)' }}>*</span>}
                          </div>
                          {subItems.map((sub, si) => (
                            <div key={sub.name + si} className={`cic-tab cic-tab-depth-${(s.depth || 0) + 2}`} style={{ pointerEvents: 'none', opacity: 0.6 }}>
                              <span className="cic-tab-tree" aria-hidden="true">
                                <span className="cic-tab-tree-glyph">{si === subItems.length - 1 ? '└' : '├'}</span>
                              </span>
                              <span className="cic-tab-label">{sub.name}</span>
                              {sub.isRequired && <span className="cic-tag" style={{ color: 'var(--accent)' }}>*</span>}
                            </div>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })
            ) : (
              <div style={{ padding: '16px 12px', fontSize: '12px', color: 'var(--ink-4)', textAlign: 'center' }}>
                <div style={{ marginBottom: '8px' }}>📋</div>
                <div>Loading P&L hierarchy...</div>
              </div>
            )}
          </aside>
          <section className="cic-main">
            {!activeSectionMeta ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px', color: 'var(--ink-4)' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
                  <div style={{ fontWeight: 500, marginBottom: '4px' }}>Loading P&L Structure</div>
                  <div style={{ fontSize: '12px' }}>Please wait while the P&L hierarchy is being loaded...</div>
                </div>
              </div>
            ) : (
              <>
                <div className="cic-main-head">
                  <div>
                    {crumbs.length > 1 && (
                      <div className="cic-crumbs">
                        {crumbs.map((c, i) => (
                          <React.Fragment key={c.id}>
                            {i > 0 && <span className="cic-crumb-sep">›</span>}
                            <span className={i === crumbs.length - 1 ? 'cic-crumb-active' : 'cic-crumb'}>{c.label}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                    <div className="cic-main-title">{activeSectionMeta?.label}</div>
                    <div className="cic-main-sub">
                      {isCalculatedLeaf && 'Calculated row — value comes from a formula.'}
                      {isFieldConfigMode && 'Built-in line item · edit field configuration.'}
                      {isGroupAddMode && (
                        <>{items.filter(it => !it.parentGroupName?.trim()).length} allowed item{items.filter(it => !it.parentGroupName?.trim()).length === 1 ? '' : 's'} for franchisees.</>
                      )}
                    </div>
                  </div>
                  {isGroupAddMode && (
                    <button className="btn primary" onClick={addRow}><Icon.plus /> &nbsp;Add Item</button>
                  )}
                </div>

                {isCalculatedLeaf ? (
                  <div className="cic-empty">
                    <div className="cic-empty-icon"><Icon.info /></div>
                    <div className="cic-empty-title">Calculated row</div>
                    <div className="cic-empty-sub"><strong>{activeSectionMeta?.label}</strong> is auto-calculated and cannot be configured.</div>
                  </div>
                ) : items.length === 0 ? (
                  <div className="cic-empty">
                    <div className="cic-empty-icon"><Icon.info /></div>
                    <div className="cic-empty-title">No items configured</div>
                    <div className="cic-empty-sub">Add allowed line items for <strong>{activeSectionMeta?.label}</strong>.</div>
                    <button className="btn primary" onClick={addRow} style={{ marginTop: 12 }}><Icon.plus /> &nbsp;Add first item</button>
                  </div>
                ) : isGroupAddMode ? (
                  // Flat list of L2 custom items; Calculated items show embedded sub-items section
                  <div className="cic-list">
                    {/* Shared column header */}
                    <div className="cic-list-header">
                      <span className="cic-col-name">Item name</span>
                      {canBeCalculated && <span className="cic-col-fixed">Type</span>}
                      <span className="cic-col-fixed">Field type</span>
                      <span className="cic-col-fixed">Max value</span>
                      <span className="cic-col-fixed">Required</span>
                      <span className="cic-col-del" />
                    </div>
                    {items.filter(it => it.parentGroupName == null).map((r, _unused) => {
                      const i = items.indexOf(r);
                      const subItems = items
                        .map((it, idx) => ({ it, idx }))
                        .filter(({ it }) => it.parentGroupName?.trim() === r.name?.trim());
                      return (
                        <div className="cic-card" key={i} style={{ borderLeft: r.isCalculated ? '3px solid var(--accent)' : undefined }}>
                          <div className="cic-card-row">
                            <div className="cic-col-name">
                              <input value={r.name} placeholder="e.g. Interest Income" onChange={(e) => updateRow(i, { name: e.target.value })} />
                            </div>
                            {canBeCalculated && (
                              <div className="cic-col-fixed">
                                <select
                                  value={r.isCalculated ? 'calculated' : 'manual'}
                                  onChange={(e) => toggleCalculated(i, e.target.value === 'calculated')}
                                >
                                  <option value="manual">Manual</option>
                                  <option value="calculated">∑ Calculated</option>
                                </select>
                              </div>
                            )}
                            <div className="cic-col-fixed">
                              {!r.isCalculated
                                ? <select value={r.fieldType} onChange={(e) => updateRow(i, { fieldType: e.target.value })}>
                                    <option value="number">Number</option>
                                    <option value="text">Text</option>
                                  </select>
                                : <span className="cic-col-na">—</span>}
                            </div>
                            <div className="cic-col-fixed">
                              {!r.isCalculated
                                ? <input type="number" value={r.max} onChange={(e) => updateRow(i, { max: +e.target.value || 0 })} />
                                : <span className="cic-col-na">—</span>}
                            </div>
                            <div className="cic-col-fixed">
                              <select
                                value={r.isRequired ? 'required' : 'optional'}
                                onChange={(e) => updateRow(i, { isRequired: e.target.value === 'required' })}
                              >
                                <option value="optional">Optional</option>
                                <option value="required">Required</option>
                              </select>
                            </div>
                            <div className="cic-col-del">
                              <button className="cic-delete" onClick={() => removeRow(i)} disabled={r.isRequired} title={r.isRequired ? 'Required — cannot delete' : 'Remove'}><Icon.trash /></button>
                            </div>
                          </div>
                          {/* Sub-items section — only shown for Calculated items */}
                          {r.isCalculated && (
                            <div className="cic-subitems">
                              <div className="cic-subitems-header">
                                <span>Sub-items <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(always Manual)</span></span>
                                <button className="btn-sm" onClick={() => addSubRow(r.name)}><Icon.plus /> Add sub-item</button>
                              </div>
                              {/* Sub-item header */}
                              {subItems.length > 0 && (
                                <div className="cic-list-header" style={{ marginBottom: 4 }}>
                                  <span className="cic-col-name">Sub-item name</span>
                                  <span className="cic-col-fixed">Field type</span>
                                  <span className="cic-col-fixed">Max value</span>
                                  <span className="cic-col-fixed">Required</span>
                                  <span className="cic-col-del" />
                                </div>
                              )}
                              {subItems.length === 0 && (
                                <div className="cic-subitems-empty">No sub-items yet. Add at least one so franchisees can enter values.</div>
                              )}
                              {subItems.map(({ it: sub, idx: subI }) => (
                                <div className="cic-subitem-row" key={subI}>
                                  <div className="cic-col-name">
                                    <input value={sub.name} placeholder="e.g. Retail" onChange={(e) => updateRow(subI, { name: e.target.value })} />
                                  </div>
                                  <div className="cic-col-fixed">
                                    <select value={sub.fieldType} onChange={(e) => updateRow(subI, { fieldType: e.target.value })}>
                                      <option value="number">Number</option>
                                      <option value="text">Text</option>
                                    </select>
                                  </div>
                                  <div className="cic-col-fixed">
                                    <input type="number" value={sub.max} placeholder="Max" onChange={(e) => updateRow(subI, { max: +e.target.value || 0 })} />
                                  </div>
                                  <div className="cic-col-fixed">
                                    <select
                                      value={sub.isRequired ? 'required' : 'optional'}
                                      onChange={(e) => updateRow(subI, { isRequired: e.target.value === 'required' })}
                                    >
                                      <option value="optional">Optional</option>
                                      <option value="required">Required</option>
                                    </select>
                                  </div>
                                  <div className="cic-col-del">
                                    <button className="cic-delete" onClick={() => { const subs = items.filter(it => it.parentGroupName?.trim() === r.name?.trim()); const si = subs.indexOf(sub); removeSubRow(r.name, si); }} disabled={sub.isRequired} title={sub.isRequired ? 'Required — cannot delete' : 'Remove'}><Icon.trash /></button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Field config mode (leaf built-in items)
                  <div className="cic-list">
                    {items.map((r, i) => (
                      <div className="cic-card" key={i}>
                        <div className="cic-card-row">
                          <div className="cic-field cic-field-name">
                            <label>Display label</label>
                            <input value={r.name} placeholder="e.g. Packaging" onChange={(e) => updateRow(i, { name: e.target.value })} />
                          </div>
                          <div className="cic-field">
                            <label>Type</label>
                            <select value={r.fieldType} onChange={(e) => updateRow(i, { fieldType: e.target.value })}>
                              <option value="number">Number</option>
                              <option value="text">Text</option>
                            </select>
                          </div>
                          <div className="cic-field">
                            <label>Max value</label>
                            <input type="number" value={r.max} onChange={(e) => updateRow(i, { max: +e.target.value || 0 })} />
                          </div>
                          <div className="cic-field cic-field-required">
                            <label>Required</label>
                            <button
                              className={`cic-toggle ${r.isRequired ? 'cic-toggle-on' : ''}`}
                              onClick={() => updateRow(i, { isRequired: !r.isRequired })}
                              title={r.isRequired ? 'Required — click to make Optional' : 'Optional — click to make Required'}
                            >
                              {r.isRequired ? 'Required' : 'Optional'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSave}>Save</button>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="modal-backdrop" style={{ zIndex: 1100 }} onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <span className="modal-title">Delete "{deleteConfirm.label}"?</span>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
                Are you sure you want to delete this line item? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn" style={{ background: 'var(--brand-red)', color: '#fff', borderColor: 'var(--brand-red)' }} onClick={deleteConfirm.onConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============== Row Drawer ===============
function RowDrawer({ row, computedByMonth, months, onClose, tree }) {
  if (!row) return null;
  const monthVals = months.map((m) => ({ m, v: computedByMonth[m]?.[row.id] ?? 0 }));
  const yearSum = monthVals.reduce((s, x) => s + x.v, 0);
  const avg = yearSum / 12;
  const max = Math.max(...monthVals.map(x => x.v), 1);
  const min = Math.min(...monthVals.map(x => x.v), 0);
  const W = 460, H = 60;
  const pts = monthVals.map((x, i) => {
    const px = (i / 11) * W;
    const py = H - ((x.v - min) / (max - min || 1)) * H;
    return [px, py];
  });
  const pathD = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p.join(' ')).join(' ');
  const fillD = pathD + ` L ${W} ${H} L 0 ${H} Z`;

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer">
        <div className="drawer-header">
          <div>
            <h3 className="drawer-title">{row.label}</h3>
            <div className="drawer-subtitle">
              <TypePill type={row.type} />
              &nbsp;·&nbsp;{row.depth === 0 ? 'Top-level' : `Depth ${row.depth + 1}`}
              {row.required && <> &nbsp;·&nbsp; Required</>}
            </div>
          </div>
          <button className="drawer-close" onClick={onClose}><Icon.close /></button>
        </div>
        <div className="drawer-body">
          <div className="drawer-section">
            <div className="drawer-section-title">Year-to-date</div>
            <div style={{ display: 'flex', gap: 20, alignItems: 'baseline' }}>
              <div style={{ fontSize: 28, fontFamily: 'var(--font-num)', fontWeight: 600, letterSpacing: '-0.01em' }}>
                {fmtMoney(yearSum)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>avg {fmtMoney(avg)} / mo</div>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="spark" style={{ marginTop: 8 }}>
              <path d={fillD} fill="var(--accent-soft)" />
              <path d={pathD} fill="none" stroke="var(--accent)" strokeWidth="1.5" />
            </svg>
          </div>
          <div className="drawer-section">
            <div className="drawer-section-title">Monthly values</div>
            <div className="month-grid">
              {monthVals.map(({ m, v }) => (
                <div key={m} className="month-chip">
                  <span className="m">{m}</span>
                  <span className="v">{fmtMoney(v)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="drawer-section">
            <div className="drawer-section-title">Properties</div>
            <div className="meta-grid">
              <div className="meta-row"><span className="k">Entry method</span><span className="v">{row.type === 'system' ? 'Auto-imported' : row.type === 'calculated' ? 'System-calculated' : 'Manual entry'}</span></div>
              <div className="meta-row"><span className="k">Required</span><span className="v">{row.required ? 'Yes' : 'No'}</span></div>
              <div className="meta-row"><span className="k">Deletable</span><span className="v">{row.deletable === 'zero' ? 'If auto-value is 0' : (row.deletable ? 'Yes' : 'No')}</span></div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

// =============== Tweaks Panel ===============
function TweaksPanel({ settings, setSettings }) {
  const hues = [
    { name: 'Cherry', h: 20 },
    { name: 'Berry', h: 345 },
    { name: 'Cobalt', h: 255 },
    { name: 'Teal', h: 190 },
    { name: 'Forest', h: 150 },
    { name: 'Slate', h: 80 },
  ];
  return (
    <div className="tweaks">
      <div className="tweaks-header">
        <span className="tweaks-title">Tweaks</span>
      </div>
      <div className="tweaks-body">
        <div className="tweak-row">
          <label>Accent</label>
          <div className="swatches">
            {hues.map((c) => (
              <button key={c.h} className={`swatch ${settings.accentH === c.h ? 'active' : ''}`}
                style={{ background: `oklch(0.58 0.21 ${c.h})` }} title={c.name}
                onClick={() => setSettings({ ...settings, accentH: c.h })} />
            ))}
          </div>
        </div>
        <div className="tweak-row">
          <label>Density</label>
          <div className="segmented">
            {['compact', 'cozy', 'roomy'].map((d) => (
              <button key={d} className={settings.density === d ? 'active' : ''} onClick={() => setSettings({ ...settings, density: d })}>
                {d[0].toUpperCase() + d.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="tweak-inline">
          <label>Show % column</label>
          <button className={`switch ${settings.showPct ? 'on' : ''}`} onClick={() => setSettings({ ...settings, showPct: !settings.showPct })} />
        </div>
        <div className="tweak-inline">
          <label>Show type pills</label>
          <button className={`switch ${settings.showTypes ? 'on' : ''}`} onClick={() => setSettings({ ...settings, showTypes: !settings.showTypes })} />
        </div>
        <div className="tweak-inline">
          <label>Dark mode</label>
          <button className={`switch ${settings.dark ? 'on' : ''}`} onClick={() => setSettings({ ...settings, dark: !settings.dark })} />
        </div>
      </div>
    </div>
  );
}

// =============== Overview Grid ===============
function OverviewGrid({ tree, expanded, toggleExpand, computedByMonth, months, year, onSelectRow = () => {}, editMonth, canEdit, onEditCell, periodStatus = {} }) {
  const rows = flattenTree(tree, expanded);
  
  // Helper: check if a month's status is submitted
  const isMonthSubmitted = (m) => {
    const monthKey = `${m}-${year}`;
    const status = periodStatus[monthKey] || 'not-started';
    return status === 'submitted';
  };
  
  // YTD calculation: sum only submitted months
  const ytdByRow = (id) => {
    return months.reduce((s, m) => {
      if (isMonthSubmitted(m)) {
        return s + (computedByMonth[m]?.[id] || 0);
      }
      return s;
    }, 0);
  };
  
  const totalRev_YTD = months.reduce((s, m) => {
    if (isMonthSubmitted(m)) {
      return s + (computedByMonth[m]?.income || 0);
    }
    return s;
  }, 0);
  
  const grossRev_YTD = months.reduce((s, m) => {
    if (isMonthSubmitted(m)) {
      return s + (computedByMonth[m]?.__gross_income || 0);
    }
    return s;
  }, 0);
  
  // Count how many months are submitted (for YTD header label)
  const submittedMonthsCount = months.filter(m => isMonthSubmitted(m)).length;

  return (
    <div className="overview-shell">
      <div className="pl-scroll">
        <table className="pl-table overview-table">
          <thead>
            <tr>
              <th className="col-category sticky" rowSpan="2">Category</th>
              {months.map((m) => (
                <th key={m} className={`col-month-sm col-month-group${m === editMonth ? ' col-month-active' : ''}`} colSpan="2">{m}{m === editMonth && canEdit ? ' ✎' : ''}</th>
              ))}
              <th className="col-ytd" rowSpan="2">YTD {year}{submittedMonthsCount > 0 && submittedMonthsCount < 12 ? ` (${submittedMonthsCount})` : ''}</th>
              <th className="col-pct-ytd" rowSpan="2">% Rev YTD</th>
            </tr>
            <tr>
              {months.map((m) => (
                <React.Fragment key={m}>
                  <th className={`col-month-sm col-month-amt${m === editMonth ? ' col-month-active' : ''}`}>$</th>
                  <th className={`col-month-sm col-month-pct${m === editMonth ? ' col-month-active' : ''}`}>%</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const hasChildren = row.children && row.children.length > 0;
              const isDeduction = isDeductionKey(row.id);
              const rowClass = [
                row.major ? 'row-major' : '',
                row.accent ? 'row-accent' : '',
                row.type === 'calculated' || hasChildren ? 'row-calculated' : '',
                isDeduction ? 'row-deduction' : '',
              ].filter(Boolean).join(' ');
              const isIncomeChild = row.parent?.id === 'income';
              const ytd = ytdByRow(row.id);
              const displayYtd = isDeduction ? -ytd : ytd;
              const pctBase_YTD = isIncomeChild ? grossRev_YTD : totalRev_YTD;
              const pct = pctBase_YTD && row.id !== 'income' ? (Math.abs(ytd) / pctBase_YTD) * 100 : null;
              return (
                <tr key={row.id} className={rowClass}>
                  <td className="col-category sticky" style={{ paddingLeft: 14 + row.depth * 16 }}>
                    <div className="cat-cell">
                      <Disclosure open={expanded.has(row.id)} onClick={() => toggleExpand(row.id)} leaf={!hasChildren} />
                      <span className="cat-text">{row.label}</span>
                    </div>
                  </td>
                  {months.map((m) => {
                    const monthSubmitted = isMonthSubmitted(m);
                    const v = computedByMonth[m]?.[row.id] ?? 0;
                    const displayV = isDeduction ? -v : v;
                    const monthRev = computedByMonth[m]?.income ?? 0;
                    const grossMonthRev = computedByMonth[m]?.__gross_income ?? monthRev;
                    const mpctBase = isIncomeChild ? grossMonthRev : monthRev;
                    const mpct = (mpctBase && row.id !== 'income') ? (Math.abs(v) / mpctBase) * 100 : null;
                    const cellEditable = canEdit && m === editMonth && (row.type === 'manual' || row.type === 'custom') && !hasChildren;
                    return (
                      <React.Fragment key={m}>
                        {cellEditable ? (
                          <NumCell
                            value={v}
                            editable={true}
                            row={row}
                            month={m}
                            onEdit={onEditCell}
                            className="col-month-sm col-month-amt col-month-active"
                          />
                        ) : (
                          <td className={`num col-month-sm col-month-amt${m === editMonth ? ' col-month-active' : ''} ${displayV < 0 ? 'neg' : ''}`} onClick={(e) => e.stopPropagation()}>
                            {!monthSubmitted ? <span style={{ color: 'var(--ink-4)' }}>—</span> : v === 0 ? <span style={{ color: 'var(--ink-4)' }}>—</span> : fmtMoney(displayV, { parens: isDeduction })}
                          </td>
                        )}
                        <td className={`num col-month-sm col-month-pct${m === editMonth ? ' col-month-active' : ''}`} onClick={(e) => e.stopPropagation()}>
                          {!monthSubmitted || mpct == null || v === 0 ? <span style={{ color: 'var(--ink-4)' }}>—</span> : isDeduction ? '(' + mpct.toFixed(2) + '%)' : mpct.toFixed(2) + '%'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td className={`num col-ytd ${displayYtd < 0 ? 'neg' : ''}`} onClick={(e) => e.stopPropagation()}>
                    {submittedMonthsCount === 0 ? <span style={{ color: 'var(--ink-4)' }}>—</span> : <strong>{fmtMoney(displayYtd, { parens: isDeduction })}</strong>}
                  </td>
                  <td className="num col-pct-ytd" onClick={(e) => e.stopPropagation()}>
                    {submittedMonthsCount === 0 || pct == null ? <span style={{ color: 'var(--ink-4)' }}>—</span> : isDeduction ? '(' + pct.toFixed(2) + '%)' : pct.toFixed(2) + '%'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============== Mode-switch warning modal ===============
function ModeSwitchWarn({ from, to, onCancel, onConfirm }) {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Switch to {to} mode?</h3>
          <div className="modal-subtitle">You're leaving {from} mode.</div>
        </div>
        <div className="modal-body">
          <div className="warn-callout">
            <Icon.info /> Detailed line item values will be <strong>cleared</strong> when you switch to Simplified. This cannot be undone.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onCancel}>Keep {from}</button>
          <button className="btn primary" onClick={onConfirm}>Switch & clear detail</button>
        </div>
      </div>
    </div>
  );
}

function fmtUSDateTime(raw) {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return raw; }
}

function ReopenRequestCard({ req, onResolve }) {
  const [adminNote, setAdminNote] = useState('');
  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Store #{req.storeNumber} — {req.monthLabel} {req.year}</div>
      <div style={{ fontSize: 13, color: '#555', marginBottom: 8 }}><strong>Reason:</strong> {req.reason}</div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Submitted by {req.requestedBy} on {fmtUSDateTime(req.requestedAt)}</div>
      <textarea
        placeholder="Admin note (optional)"
        value={adminNote}
        onChange={e => setAdminNote(e.target.value)}
        rows={2}
        style={{ width: '100%', fontSize: 13, borderRadius: 4, border: '1px solid #ccc', padding: '4px 8px', marginBottom: 8, boxSizing: 'border-box' }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary" style={{ fontSize: 13 }} onClick={() => onResolve(req, 'approve', adminNote)}>
          Approve &amp; Reopen
        </button>
        <button className="btn" style={{ fontSize: 13 }} onClick={() => onResolve(req, 'deny', adminNote)}>
          Deny
        </button>
      </div>
    </div>
  );
}

// =============== LineItemsConfigPanel (Super Admin only) ===============
// 5 fixed section tabs. Per-row: Label, Max Value, Sort Order, Active, Required, Manual/Auto, Delete.
// Manual items can have sub-items (L3). isCalculated is DERIVED on save (true when sub-items exist).
// allowAdd is a UI concern (calculated roots can't have sub-items); labels come from the tree (DB-driven).
const LINE_ITEMS_SECTIONS = [
  { key: 'income',       allowAdd: true  },
  { key: 'cogs',         allowAdd: true  },
  { key: 'gross_profit', allowAdd: false },
  { key: 'expenses',     allowAdd: true  },
  { key: 'net_income',   allowAdd: false },
];

function LineItemsConfigPanel({ schemas, sections, onUpdate, onDone, onClose, apiKeyNames = [] }) {
  const [activeSection, setActiveSection] = useState('income');

  // Enrich LINE_ITEMS_SECTIONS with DB-driven labels from the tree (sections prop).
  // Falls back to the lineKey itself if the tree hasn't loaded yet.
  const enrichedSections = LINE_ITEMS_SECTIONS.map(s => ({
    ...s,
    label: sections?.find(sec => sec.id === s.key)?.label || s.key,
  }));

  const [draftSchemas, setDraftSchemas] = useState(() => {
    // Deep-clone the schemas so we never mutate the prop
    const clone = {};
    Object.entries(schemas || {}).forEach(([k, v]) => {
      clone[k] = v ? v.map(item => ({ ...item })) : [];
    });
    return clone;
  });
  const [dupWarning, setDupWarning] = useState(null);
  // dupWarning: { sectionKey, rowIdx, key, conflictSectionKey, conflictLabel } | null
  const [removeSubsWarn, setRemoveSubsWarn] = useState(null);
  // removeSubsWarn: { count, onConfirm } | null — shown when switching Manual→Auto while sub-items exist
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // deleteConfirm: { label, subCount, onConfirm } | null
  const [saving, setSaving] = useState(false);
  const [noNameWarn, setNoNameWarn] = useState(null); // rowIdx that was clicked without a name

  const activeSectionMeta = enrichedSections.find(s => s.key === activeSection);
  const items = draftSchemas[activeSection] || [];
  // Strict null check — empty-string parentGroupName (from addL3Row with unnamed parent) must NOT leak into L2
  const l2Items = items.filter(it => it.parentGroupName == null).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  // ---- helpers ----
  const updateItem = (sectionKey, idx, patch) => {
    setDraftSchemas(prev => {
      const current = prev[sectionKey] || [];
      const oldItem = current[idx];
      const newItem = { ...oldItem, ...patch };
      // If the name changed, cascade the update to all L3 sub-items that reference this item as parent
      let updated = current.map((r, i) => i === idx ? newItem : r);
      if ('name' in patch && oldItem && oldItem.parentGroupName == null) {
        const oldName = oldItem.name?.trim() || '';
        const newName = (patch.name || '').trim();
        if (oldName && newName && oldName !== newName) {
          updated = updated.map(r =>
            r.parentGroupName != null && r.parentGroupName.trim() === oldName
              ? { ...r, parentGroupName: patch.name }
              : r
          );
        }
      }
      return { ...prev, [sectionKey]: updated };
    });
  };

  const addL2Row = () => {
    setDraftSchemas(prev => ({
      ...prev,
      [activeSection]: [...(prev[activeSection] || []), {
        name: '', fieldType: 'number', maxLength: 9, max: 0,
        sortOrder: (prev[activeSection] || []).filter(i => !i.parentGroupName).length + 1,
        isRequired: false, isCalculated: false, parentGroupName: null, apiDataKey: null, isActive: true,
      }],
    }));
  };

  const addL3Row = (parentName, parentIdx) => {
    if (!parentName?.trim()) {
      setNoNameWarn(parentIdx);
      setTimeout(() => setNoNameWarn(null), 2500);
      return;
    }
    setNoNameWarn(null);
    setDraftSchemas(prev => ({
      ...prev,
      [activeSection]: [...(prev[activeSection] || []), {
        name: '', fieldType: 'number', maxLength: 9, max: 0,
        sortOrder: (prev[activeSection] || []).filter(i => i.parentGroupName?.trim() === parentName).length + 1,
        isRequired: false, isCalculated: false, parentGroupName: parentName, apiDataKey: null, isActive: true,
      }],
    }));
  };

  const removeL2Row = (idx) => {
    const item = items[idx];
    if (!item) return;
    const subCount = items.filter(i => i.parentGroupName?.trim() === item.name?.trim()).length;
    const doRemove = () => {
      setDraftSchemas(prev => ({
        ...prev,
        [activeSection]: (prev[activeSection] || []).filter((it, i) => {
          if (i === idx) return false;
          if (it.parentGroupName?.trim() === item.name?.trim()) return false;
          return true;
        }),
      }));
      setDeleteConfirm(null);
    };
    setDeleteConfirm({ label: item.name || 'this item', subCount, onConfirm: doRemove });
  };

  const removeL3Row = (parentName, subIdx) => {
    const current = draftSchemas[activeSection] || [];
    const subItems = current.filter(i => i.parentGroupName?.trim() === parentName);
    const item = subItems[subIdx];
    if (!item) return;
    setDeleteConfirm({
      label: item.name || 'this item',
      subCount: 0,
      onConfirm: () => {
        setDraftSchemas(prev => {
          const cur = prev[activeSection] || [];
          const subs = cur.filter(i => i.parentGroupName?.trim() === parentName);
          const toRemove = subs[subIdx];
          return { ...prev, [activeSection]: cur.filter(i => i !== toRemove) };
        });
        setDeleteConfirm(null);
      },
    });
  };

  const switchToAuto = (idx) => {
    const item = items[idx];
    if (!item) return;
    const subCount = items.filter(i => i.parentGroupName?.trim() === item.name?.trim()).length;
    const doSwitch = () => {
      const firstKey = apiKeyNames[0];
      setDraftSchemas(prev => {
        const current = prev[activeSection] || [];
        let updated = current.map((it, i) =>
          i === idx ? { ...it, isCalculated: false, apiDataKey: it.apiDataKey || firstKey } : it
        );
        // Remove any sub-items of this row
        updated = updated.filter(it => it.parentGroupName?.trim() !== item.name?.trim());
        return { ...prev, [activeSection]: updated };
      });
      setRemoveSubsWarn(null);
    };
    if (subCount > 0) {
      setRemoveSubsWarn({ count: subCount, onConfirm: doSwitch });
    } else {
      doSwitch();
    }
  };

  const switchToManual = (idx) => {
    updateItem(activeSection, idx, { apiDataKey: null });
  };

  const setApiKey = (sectionKey, rowIdx, key) => {
    // If clearing the key (empty / null), just update directly — no conflict check needed
    if (!key) {
      updateItem(sectionKey, rowIdx, { apiDataKey: null });
      return;
    }
    let conflictSectionKey = null;
    let conflictLabel = null;
    for (const sk of Object.keys(draftSchemas)) {
      const sItems = draftSchemas[sk] || [];
      const found = sItems.find((it, i) => !(sk === sectionKey && i === rowIdx) && it.apiDataKey === key);
      if (found) { conflictSectionKey = sk; conflictLabel = found.name; break; }
    }
    if (conflictLabel) {
      setDupWarning({ sectionKey, rowIdx, key, conflictSectionKey, conflictLabel });
    } else {
      updateItem(sectionKey, rowIdx, { apiDataKey: key });
    }
  };

  const handleDupAssignAnyway = () => {
    if (!dupWarning) return;
    const { sectionKey, rowIdx, key, conflictSectionKey, conflictLabel } = dupWarning;
    setDraftSchemas(prev => {
      const updated = { ...prev };
      updated[conflictSectionKey] = (prev[conflictSectionKey] || []).map(it =>
        it.apiDataKey === key && it.name === conflictLabel ? { ...it, apiDataKey: null } : it
      );
      updated[sectionKey] = (prev[sectionKey] || []).map((it, i) =>
        i === rowIdx ? { ...it, apiDataKey: key } : it
      );
      return updated;
    });
    setDupWarning(null);
  };

  const handleSave = async () => {
    setSaving(true);
    // isCalculated is DERIVED: true when sub-items exist, false otherwise
    const normalize = (arr) => {
      const list = (arr || []).filter(it => it.name && it.name.trim() !== '');
      return list.map(it => {
        const pg = it.parentGroupName?.trim() || null;
        const hasSubItems = !pg && list.some(other => other.parentGroupName?.trim() === it.name?.trim());
        return {
          ...it,
          name: it.name.trim(),
          parentGroupName: pg,
          isCalculated: pg ? false : hasSubItems,
          apiDataKey: hasSubItems ? null : (it.apiDataKey || null),
        };
      });
    };
    try {
      const changedSections = Object.entries(draftSchemas).filter(([sk, draftItems]) => {
        return JSON.stringify(normalize(draftItems)) !== JSON.stringify(normalize(schemas[sk] || []));
      });
      for (const [sk, draftItems] of changedSections) {
        await onUpdate(sk, normalize(draftItems));
      }
      if (changedSections.length > 0 && onDone) await onDone();
      onClose();
    } catch (e) {
      console.error('Failed to save line items:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal cic-modal"
        style={{ maxWidth: 1100, width: '95vw', position: 'relative' }}
        onClick={e => e.stopPropagation()}
      >
        {saving && (
          <div className="pl-loading-overlay" style={{ borderRadius: 'var(--radius)', zIndex: 50, pointerEvents: 'all' }}>
            <div className="pl-loading-spinner" />
          </div>
        )}
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Line Items Configuration</h3>
            <div className="modal-subtitle">Configure P&L line items that franchisees will see and enter values for.</div>
          </div>
          <button className="btn-icon-sm" onClick={onClose} title="Close" style={{ flexShrink: 0, marginTop: 2 }}>✕</button>
        </div>
        <div className="cic-body">
          {/* Left: 5 fixed section tabs with item names */}
          <aside className="cic-sidebar" style={{ minWidth: 210, maxWidth: 230, overflowY: 'auto' }}>
            <div className="cic-sidebar-title">P&amp;L Sections</div>
            {enrichedSections.map(s => {
              const sectionItems = (draftSchemas[s.key] || []).filter(it => !it.parentGroupName?.trim()).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
              const isActive = activeSection === s.key;
              return (
                <div key={s.key}>
                  <button
                    className={`cic-tab cic-tab-depth-0 ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveSection(s.key)}
                    style={{ width: '100%' }}
                  >
                    <span className="cic-tab-label" style={{ fontWeight: 600 }}>{s.label}</span>
                    {s.allowAdd
                      ? <span className="cic-count">{sectionItems.length}</span>
                      : <span className="cic-tag">∑</span>}
                  </button>
                  {/* Item names under the section — tree style with L3 sub-items */}
                  {s.allowAdd && sectionItems.length > 0 && (
                    <div style={{ paddingLeft: 8, paddingBottom: 2 }}>
                      {sectionItems.map((it, ii) => {
                        const allItems = draftSchemas[s.key] || [];
                        const parentName = it.name?.trim() || '';
                        const subItems = parentName
                          ? allItems.filter(r => r.parentGroupName != null && r.parentGroupName.trim() === parentName).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                          : [];
                        const isLast = ii === sectionItems.length - 1;
                        return (
                          <div key={ii}>
                            {/* L2 item row */}
                            <div
                              onClick={() => setActiveSection(s.key)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 12,
                                color: isActive ? 'var(--accent)' : 'var(--ink-3)',
                                padding: '6px 4px 6px 4px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                borderRadius: 4,
                                background: isActive ? 'oklch(0.94 0.04 25)' : 'transparent',
                                fontWeight: isActive ? 600 : 400,
                              }}
                              title={it.name || 'Unnamed'}
                            >
                              <span style={{ color: 'var(--ink-4)', fontFamily: 'monospace', flexShrink: 0 }}>
                                {isLast && subItems.length === 0 ? '└' : '├'}
                              </span>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {it.name?.trim() || <em style={{ color: 'var(--ink-4)' }}>Unnamed</em>}
                              </span>
                              {subItems.length > 0 && <span style={{ fontSize: 10, color: 'var(--ink-4)', flexShrink: 0 }}>:{subItems.length}</span>}
                            </div>
                            {/* L3 sub-items */}
                            {subItems.map((sub, si) => {
                              const isLastSub = si === subItems.length - 1;
                              return (
                                <div
                                  key={si}
                                  onClick={() => setActiveSection(s.key)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 11,
                                    color: isActive ? 'var(--accent)' : 'var(--ink-4)',
                                    padding: '5px 4px 5px 14px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    borderRadius: 4,
                                    background: isActive ? 'oklch(0.96 0.02 25)' : 'transparent',
                                  }}
                                  title={sub.name || 'Unnamed'}
                                >
                                  <span style={{ color: 'var(--ink-4)', fontFamily: 'monospace', flexShrink: 0 }}>
                                    {isLastSub ? '└' : '├'}
                                  </span>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {sub.name?.trim() || <em style={{ color: 'var(--ink-4)' }}>Unnamed</em>}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </aside>

          {/* Right: item list */}
          <section className="cic-main">
            <div className="cic-main-head">
              <div>
                <div className="cic-main-title">{activeSectionMeta?.label}</div>
                <div className="cic-main-sub">
                  {activeSectionMeta?.allowAdd
                    ? <>{l2Items.length} Line Item{l2Items.length === 1 ? '' : 's'} Configured.</>
                    : 'Calculated root — value derived from formula. No sub-items allowed.'}
                </div>
              </div>
              {activeSectionMeta?.allowAdd && (
                <button className="btn primary" onClick={addL2Row}><Icon.plus /> &nbsp;Add Item</button>
              )}
            </div>

            {!activeSectionMeta?.allowAdd ? (
              <div className="cic-empty">
                <div className="cic-empty-icon"><Icon.info /></div>
                <div className="cic-empty-title">Calculated row</div>
                <div className="cic-empty-sub"><strong>{activeSectionMeta?.label}</strong> is auto-calculated and cannot have sub-items.</div>
              </div>
            ) : l2Items.length === 0 ? (
              <div className="cic-empty">
                <div className="cic-empty-icon"><Icon.info /></div>
                <div className="cic-empty-title">No items configured</div>
                <div className="cic-empty-sub">Add line items for <strong>{activeSectionMeta?.label}</strong> that franchisees will enter values for.</div>
                <button className="btn primary" onClick={addL2Row} style={{ marginTop: 12 }}><Icon.plus /> &nbsp;Add first item</button>
              </div>
            ) : (
              <div className="cic-list">
                {/* Column headers — same pattern as CustomItemsConfigPanel */}
                <div className="cic-list-header">
                  <span className="cic-col-name">Item name</span>
                  <span className="cic-col-fixed">Type</span>
                  <span className="cic-col-key">Key map</span>
                  <span className="cic-col-sm">Sort</span>
                  <span className="cic-col-sm">Max value</span>
                  <span className="cic-col-fixed">Required</span>
                  <span className="cic-col-del" />
                </div>

                {l2Items.map((r) => {
                  const globalIdx = items.findIndex(it => it === r);
                  const isAutoMode = !!r.apiDataKey;
                  // Only match sub-items when parent has a non-empty name to avoid false matches
                  const parentNameTrimmed = r.name?.trim() || '';
                  const subItems = parentNameTrimmed
                    ? items.filter(it => it.parentGroupName != null && it.parentGroupName.trim() === parentNameTrimmed).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                    : [];
                  const hasSubItems = subItems.length > 0;
                  return (
                    <div
                      className="cic-card"
                      key={`l2-${globalIdx}`}
                      style={{ borderLeft: hasSubItems ? '3px solid var(--accent)' : undefined }}
                    >
                      <div className="cic-card-row">
                        {/* Item name */}
                        <div className="cic-col-name">
                          <input
                            value={r.name}
                            placeholder="e.g. Utilities"
                            onChange={e => updateItem(activeSection, globalIdx, { name: e.target.value })}
                          />
                        </div>
                        {/* Type: Manual / Auto (API) — always selectable; switching to Auto while sub-items exist shows warning */}
                        <div className="cic-col-fixed">
                          <select
                            value={isAutoMode ? 'auto' : 'manual'}
                            onChange={e => {
                              if (e.target.value === 'auto') switchToAuto(globalIdx);
                              else switchToManual(globalIdx);
                            }}
                          >
                            <option value="manual">Manual</option>
                            <option value="auto">Auto (API)</option>
                          </select>
                        </div>
                        {/* Key map — API key dropdown for Auto; dash for Manual */}
                        <div className="cic-col-key">
                          {isAutoMode
                            ? <select
                                value={r.apiDataKey || ''}
                                onChange={e => setApiKey(activeSection, globalIdx, e.target.value || null)}
                              >
                                <option value="">— select key —</option>
                                {apiKeyNames.map(k => (
                                  <option key={k} value={k}>{k}</option>
                                ))}
                              </select>
                            : <span className="cic-col-na">—</span>
                          }
                        </div>
                        {/* Sort order */}
                        <div className="cic-col-sm">
                          <input type="number" min="0" placeholder="0" value={r.sortOrder || ''} onChange={e => updateItem(activeSection, globalIdx, { sortOrder: e.target.value === '' ? 0 : +e.target.value })} />
                        </div>
                        {/* Max value */}
                        <div className="cic-col-sm">
                          {isAutoMode
                            ? <span className="cic-col-na">—</span>
                            : <input
                                type="number"
                                min="0"
                                placeholder="0"
                                value={r.max || ''}
                                onChange={e => updateItem(activeSection, globalIdx, { max: e.target.value === '' ? 0 : +e.target.value })}
                              />
                          }
                        </div>
                        {/* Required */}
                        <div className="cic-col-fixed">
                          <select
                            value={r.isRequired ? 'required' : 'optional'}
                            onChange={e => updateItem(activeSection, globalIdx, { isRequired: e.target.value === 'required' })}
                          >
                            <option value="optional">Optional</option>
                            <option value="required">Required</option>
                          </select>
                        </div>
                        {/* Delete */}
                        <div className="cic-col-del">
                          <button className="cic-delete" onClick={() => removeL2Row(globalIdx)} disabled={r.isRequired} title="Remove"><Icon.trash /></button>
                        </div>
                      </div>

                      {/* Sub-items — available for ALL Manual items */}
                      {!isAutoMode && (
                        <div className="cic-subitems">
                          <div className="cic-subitems-header">
                            <span>Sub-items</span>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                              <button
                                type="button"
                                className="btn-sm"
                                onClick={() => addL3Row(r.name, globalIdx)}
                              ><Icon.plus /> Add sub-item</button>
                              {noNameWarn === globalIdx && (
                                <span style={{ fontSize: 11, color: 'var(--brand-red)', whiteSpace: 'nowrap' }}>Enter a Display Label first</span>
                              )}
                            </div>
                          </div>
                          {hasSubItems && (
                            <div className="cic-list-header" style={{ marginBottom: 4 }}>
                              <span className="cic-col-name">Sub-item name</span>
                              <span className="cic-col-fixed">Type</span>
                              <span className="cic-col-key">Key map</span>
                              <span className="cic-col-sm">Sort</span>
                              <span className="cic-col-sm">Max value</span>
                              <span className="cic-col-fixed">Required</span>
                              <span className="cic-col-del" />
                            </div>
                          )}
                          {!hasSubItems && (
                            <div className="cic-subitems-empty">No sub-items yet — add sub-items to make this a summing parent row.</div>
                          )}
                          {subItems.map((sub, si) => {
                            const subGlobalIdx = items.findIndex(it => it === sub);
                            const subIsAuto = sub.apiDataKey != null; // null=manual, ''=auto-unset, 'key'=auto-set
                            return (
                              <div className="cic-subitem-row" key={si}>
                                <div className="cic-col-name">
                                  <input
                                    value={sub.name}
                                    placeholder="e.g. Electric"
                                    onChange={e => updateItem(activeSection, subGlobalIdx, { name: e.target.value })}
                                  />
                                </div>
                                {/* Type: Manual / Auto (API) */}
                                <div className="cic-col-fixed">
                                  <select
                                    value={subIsAuto ? 'auto' : 'manual'}
                                    onChange={e => {
                                      if (e.target.value === 'auto') updateItem(activeSection, subGlobalIdx, { apiDataKey: '' });
                                      else updateItem(activeSection, subGlobalIdx, { apiDataKey: null });
                                    }}
                                  >
                                    <option value="manual">Manual</option>
                                    <option value="auto">Auto (API)</option>
                                  </select>
                                </div>
                                {/* Key map */}
                                <div className="cic-col-key">
                                  {subIsAuto
                                    ? <select
                                        value={sub.apiDataKey || ''}
                                        onChange={e => setApiKey(activeSection, subGlobalIdx, e.target.value || '')}
                                      >
                                        <option value="">— select key —</option>
                                        {apiKeyNames.map(k => (
                                          <option key={k} value={k}>{k}</option>
                                        ))}
                                      </select>
                                    : <span className="cic-col-na">—</span>
                                  }
                                </div>
                                {/* Sort order */}
                                <div className="cic-col-sm">
                                  <input type="number" min="0" placeholder="0" value={sub.sortOrder || ''} onChange={e => updateItem(activeSection, subGlobalIdx, { sortOrder: e.target.value === '' ? 0 : +e.target.value })} />
                                </div>
                                <div className="cic-col-sm">
                                  <input type="number" min="0" placeholder="0" value={sub.max || ''} onChange={e => updateItem(activeSection, subGlobalIdx, { max: e.target.value === '' ? 0 : +e.target.value })} />
                                </div>
                                <div className="cic-col-fixed">
                                  <select
                                    value={sub.isRequired ? 'required' : 'optional'}
                                    onChange={e => updateItem(activeSection, subGlobalIdx, { isRequired: e.target.value === 'required' })}
                                  >
                                    <option value="optional">Optional</option>
                                    <option value="required">Required</option>
                                  </select>
                                </div>
                                <div className="cic-col-del">
                                  <button className="cic-delete" onClick={() => removeL3Row(r.name, si)} disabled={sub.isRequired} title="Remove"><Icon.trash /></button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Duplicate API key warning */}
      {dupWarning && (() => {
        const conflictSection = enrichedSections.find(s => s.key === dupWarning.conflictSectionKey);
        return (
          <div className="modal-backdrop" style={{ zIndex: 1100 }} onClick={() => setDupWarning(null)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
              <div className="modal-header">
                <span className="modal-title">Key "{dupWarning.key}" already assigned</span>
                <button className="modal-close" onClick={() => setDupWarning(null)}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
                  Key <strong>{dupWarning.key}</strong> is already assigned to <strong>{dupWarning.conflictLabel}</strong>
                  {conflictSection ? <> in <strong>{conflictSection.label}</strong></> : null}.
                  Please select a different key.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn primary" onClick={() => setDupWarning(null)}>OK</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete confirmation dialog */}
      {deleteConfirm && (
        <div className="modal-backdrop" style={{ zIndex: 1100 }} onClick={() => setDeleteConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <span className="modal-title">Delete "{deleteConfirm.label}"?</span>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
                Are you sure you want to delete this line item? This action cannot be undone.
                {deleteConfirm.subCount > 0 && (
                  <> This will also remove <strong>{deleteConfirm.subCount} sub-item{deleteConfirm.subCount === 1 ? '' : 's'}</strong>.</>
                )}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn" style={{ background: 'var(--brand-red)', color: '#fff', borderColor: 'var(--brand-red)' }} onClick={deleteConfirm.onConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Remove sub-items warning (switching to Auto / deleting parent) */}
      {removeSubsWarn && (
        <div className="modal-backdrop" style={{ zIndex: 1100 }} onClick={() => setRemoveSubsWarn(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <span className="modal-title">Remove sub-items?</span>
              <button className="modal-close" onClick={() => setRemoveSubsWarn(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
                Switching to <strong>Auto (API)</strong> will remove <strong>{removeSubsWarn.count} sub-item{removeSubsWarn.count === 1 ? '' : 's'}</strong> under this item. This cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setRemoveSubsWarn(null)}>Cancel</button>
              <button className="btn primary" onClick={removeSubsWarn.onConfirm}>Remove All</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { flattenTree, computeMonth, yearTotals, collectNodes, PLRow, AddLineModal, CustomItemsConfigPanel, LineItemsConfigPanel, RowDrawer, TweaksPanel, OverviewGrid, ModeSwitchWarn, ReopenRequestCard };
